import { useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { HeartPulse, LockKeyhole, ShieldCheck } from 'lucide-react'
import toast from 'react-hot-toast'

import { useAuth } from '@/context/AuthContext'
import { checkSupabaseAuthReachability } from '@/lib/supabase-auth-errors'

type AuthMode = 'signin' | 'signup'

export default function Login() {
  const { user, signIn, signUp, loading, isDemo } = useAuth()
  const [mode, setMode] = useState<AuthMode>('signin')
  const [fullName, setFullName] = useState('Camille')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [authIssue, setAuthIssue] = useState<string | null>(null)

  useEffect(() => {
    if (isDemo) {
      setAuthIssue(null)
      return
    }

    let cancelled = false

    async function runReachabilityCheck() {
      const result = await checkSupabaseAuthReachability()

      if (!cancelled) {
        setAuthIssue(result.reachable ? null : result.message)
      }
    }

    void runReachabilityCheck()

    return () => {
      cancelled = true
    }
  }, [isDemo])

  if (loading) {
    return (
      <div className="min-h-screen bg-dark-bg flex items-center justify-center">
        <div className="spinner" />
      </div>
    )
  }

  if (user) {
    return <Navigate to="/" replace />
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setSubmitting(true)

    try {
      if (mode === 'signin') {
        await signIn(email, password)
        toast.success('Connexion reussie')
      } else {
        const result = await signUp({
          email,
          password,
          fullName,
        })

        toast.success(
          result.needsEmailConfirmation
            ? 'Compte cree. Verifiez votre email pour activer l acces.'
            : 'Compte cree et connecte avec succes.',
        )
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Une erreur est survenue'
      toast.error(message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-dark-bg relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(59,130,246,0.16),_transparent_35%),radial-gradient(circle_at_bottom_right,_rgba(14,165,233,0.18),_transparent_35%)]" />

      <div className="relative min-h-screen grid lg:grid-cols-[1.1fr_0.9fr]">
        <section className="hidden lg:flex flex-col justify-between p-12 border-r border-dark-border">
          <div className="space-y-6">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary-500 to-cyan-500 flex items-center justify-center shadow-glow-sm">
              <HeartPulse className="w-8 h-8 text-white" />
            </div>

            <div className="space-y-4 max-w-xl">
              <p className="text-sm uppercase tracking-[0.3em] text-primary-300">MedTrack Pro</p>
              <h1 className="text-5xl font-bold text-white leading-tight">
                Un suivi sante centralise, lisible et securise.
              </h1>
              <p className="text-lg text-slate-300 leading-relaxed">
                Chaque jour, Camille enregistre son etat dans une seule base Supabase.
                Le tableau de bord, le journal et la vue medecin vont ensuite piocher dans cette table centrale.
              </p>
            </div>
          </div>

          <div className="grid gap-4">
            {[
              {
                icon: ShieldCheck,
                title: 'Acces protege',
                text: 'Connexion obligatoire et politiques RLS pour limiter les donnees a la bonne personne.',
              },
              {
                icon: LockKeyhole,
                title: 'Service key hors frontend',
                text: 'Seule la cle publique anon est utilisee dans cette application Vite.',
              },
            ].map((item) => {
              const Icon = item.icon

              return (
                <div key={item.title} className="card bg-dark-surface/70">
                  <div className="flex items-start gap-3">
                    <div className="w-11 h-11 rounded-xl bg-primary-900/40 border border-primary-800/50 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-primary-300" />
                    </div>
                    <div className="space-y-1">
                      <h2 className="text-white font-semibold">{item.title}</h2>
                      <p className="text-sm text-slate-300">{item.text}</p>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </section>

        <section className="flex items-center justify-center p-6 lg:p-12">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full max-w-md card"
          >
            <div className="flex rounded-xl bg-dark-surface-high p-1 mb-6">
              {([
                { value: 'signin', label: 'Connexion' },
                { value: 'signup', label: 'Creation' },
              ] as const).map((item) => (
                <button
                  key={item.value}
                  type="button"
                  onClick={() => setMode(item.value)}
                  className={`flex-1 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                    mode === item.value
                      ? 'bg-primary-600 text-white'
                      : 'text-slate-300 hover:text-white'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>

            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-white">
                {mode === 'signin' ? 'Acceder au suivi sante' : 'Creer le compte patient'}
              </h2>
              <p className="text-sm text-slate-400 mt-2">
                {isDemo
                  ? 'Mode demo detecte: configurez Supabase pour activer la vraie sauvegarde.'
                  : 'Les donnees seront stockees dans votre projet Supabase personnel.'}
              </p>
            </div>

            {authIssue && (
              <div className="mb-6 rounded-xl border border-rose-500/30 bg-rose-500/10 p-4">
                <p className="text-sm font-semibold text-rose-200">Serveur de connexion indisponible</p>
                <p className="mt-2 text-sm text-rose-100/85">{authIssue}</p>
              </div>
            )}

            <form className="space-y-4" onSubmit={handleSubmit}>
              {mode === 'signup' && (
                <div>
                  <label className="block text-sm text-slate-300 mb-2" htmlFor="fullName">
                    Nom complet
                  </label>
                  <input
                    id="fullName"
                    className="input"
                    value={fullName}
                    onChange={(event) => setFullName(event.target.value)}
                    placeholder="Camille Doux"
                    required
                  />
                </div>
              )}

              <div>
                <label className="block text-sm text-slate-300 mb-2" htmlFor="email">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  className="input"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="camille@email.com"
                  required
                />
              </div>

              <div>
                <label className="block text-sm text-slate-300 mb-2" htmlFor="password">
                  Mot de passe
                </label>
                <input
                  id="password"
                  type="password"
                  className="input"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="Minimum 6 caracteres"
                  minLength={6}
                  required
                />
              </div>

              <button className="btn-primary w-full" type="submit" disabled={submitting}>
                {submitting ? 'Chargement...' : mode === 'signin' ? 'Se connecter' : 'Creer le compte'}
              </button>
            </form>
          </motion.div>
        </section>
      </div>
    </div>
  )
}
